/* eslint-disable security/detect-object-injection */
/* eslint-disable camelcase */
import { FC, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Switch,
  FormControlLabel,
} from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import { useNotify, useTranslate, useDataProvider } from 'react-admin'
import { xxllncColor } from '../layout/themes'
import { clearSettingsCache } from '../utils/useOrganizationSettings'

interface Configuration {
  id: string;
  name?: string;
  organization_type?: string;
  sync_to_zaaksysteem?: boolean;
  field_mapping?: Record<string, string>;
}

const MAPPING_OPTIONS = [
  {
    id: 'ztc_contactpersoon_achternaam',
    label: 'Naam contactpersoon',
  },
  { id: 'bel_telefoonnummer', label: 'Telefoonnummer' },
  { id: 'ztc_onderwerp', label: 'Onderwerp' },
  { id: 'bel_extra_informatie', label: 'Extra informatie' },
]

const Settings: FC = () => {
  const translate = useTranslate()
  const notify = useNotify()
  const dataProvider = useDataProvider()

  const [configId, setConfigId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const [orgName, setOrgName] = useState('')
  const [orgType, setOrgType] = useState('')
  const [syncToZaaksysteem, setSyncToZaaksysteem] = useState(false)
  const [fieldMapping, setFieldMapping] = useState<Record<string, string>>({
    name: '',
    phone: '',
    subject: '',
    extra: '',
  })

  const applySettings = (config: Configuration) => {
    if (config) {
      setConfigId(config.id)
      setOrgName(config.name ?? '')
      setOrgType(config.organization_type ?? '')
      setSyncToZaaksysteem(config.sync_to_zaaksysteem ?? false)
      if (config.field_mapping) {
        setFieldMapping(config.field_mapping)
      }
    }
  }

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data } = await dataProvider.getList<Configuration>(
          'configuration',
          {
            pagination: { page: 1, perPage: 1 },
            sort: { field: 'id', order: 'ASC' },
            filter: {},
          },
        )
        if (data && data.length > 0) {
          applySettings(data[0])
        }
      } catch (err) {
        notify('ra.notification.http_error', { type: 'error' })
      } finally {
        setLoading(false)
      }
    }
    void fetchSettings()
  }, [dataProvider, notify])

  const saveConfiguration = async (updatedSyncStatus?: boolean) => {
    if (!configId) return

    const isSyncActive =
      updatedSyncStatus !== undefined ? updatedSyncStatus : syncToZaaksysteem

    setSaving(true)
    try {
      const { data } = await dataProvider.update<Configuration>(
        'configuration',
        {
          id: configId,
          data: {
            name: orgName,
            organization_type: orgType,
            sync_to_zaaksysteem: isSyncActive,
            field_mapping: fieldMapping,
          },
          previousData: {},
        },
      )

      applySettings(data)
      clearSettingsCache()
      notify(translate('settings.saved'), { type: 'success' })
    } catch (err) {
      notify('ra.notification.http_error', { type: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const handleSwitchChange = (checked: boolean) => {
    setSyncToZaaksysteem(checked)
    if (!checked) {
      void saveConfiguration(false)
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ maxWidth: 700, mt: 2, ml: 2, pb: 4 }}>
      <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
        Synchronisatie
      </Typography>

      <Card sx={{ mb: 3, boxShadow: '0px 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent>
          <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={syncToZaaksysteem}
                  onChange={(e) => handleSwitchChange(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography sx={{ fontWeight: 500 }}>
                  Zaaksysteem synchronisatie
                </Typography>
              }
            />
          </Box>
        </CardContent>
      </Card>

      {syncToZaaksysteem && (
        <Card sx={{ mb: 3, boxShadow: '0px 2px 8px rgba(0,0,0,0.1)' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
              Terugbelverzoek aanmaken
            </Typography>
            {['name', 'phone', 'subject', 'extra'].map((key) => (
              <FormControl key={key} fullWidth sx={{ mb: 2 }}>
                <InputLabel>
                  {key.charAt(0).toUpperCase() + key.slice(1)} veld
                </InputLabel>
                <Select
                  value={fieldMapping[key] ?? ''}
                  label={`${key.charAt(0).toUpperCase() + key.slice(1)} veld`}
                  onChange={(e) =>
                    setFieldMapping((prev) => ({
                      ...prev,
                      [key]: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="">
                    <Typography> Geen mapping </Typography>
                  </MenuItem>
                  {MAPPING_OPTIONS.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ))}

            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1 }}>
              <Button
                variant="contained"
                endIcon={
                  saving ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <SaveIcon sx={{ fontSize: 16 }} />
                  )
                }
                onClick={() => void saveConfiguration()}
                disabled={saving}
                sx={{
                  'bgcolor': xxllncColor.veniceBlue,
                  'color': '#fff',
                  'borderRadius': 10,
                  'textTransform': 'uppercase',
                  '&:hover': { bgcolor: xxllncColor.veniceBlue, opacity: 0.9 },
                }}
              >
                Opslaan
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  )
}

export default Settings
